import { rebuildWithCopyableEdit } from '@littlebird/markdown-utils';
import { type Message, useStores } from '@littlebird/sdk';
import { ImageGenerationEvents } from '@littlebird/ui-utils';
import { useCallback } from 'react';

import { trackEvent } from '@/utils/analytics';

/** Edit, quote, and image-analytics callbacks shared by every assistant message surface */
export const useAssistantMessageCallbacks = (
  threadId: string,
  message: Message
) => {
  const { drafts, messages } = useStores();

  const handleCopyableEdit = useCallback(
    (segmentIndex: number, newText: string) => {
      const newContent = rebuildWithCopyableEdit(
        message.displayContent,
        segmentIndex,
        newText
      );
      void messages.updateMessageContent(threadId, message.id, newContent);
    },
    [messages, message, threadId]
  );

  const handleAskSelection = useCallback(
    ({ text }: { text: string }) => {
      drafts.appendSelectedText(threadId, text);
    },
    [drafts, threadId]
  );

  const handleImageLoaded = useCallback(
    (fileId: string, loadTimeMs: number) => {
      trackEvent(ImageGenerationEvents.COMPLETED, {
        message_id: message.id,
        file_id: fileId,
        chat_id: threadId,
        time_to_complete_ms: loadTimeMs,
      });
    },
    [message.id, threadId]
  );

  const handleImageError = useCallback(
    (fileId: string, error: Error, timeToFailureMs: number) => {
      trackEvent(ImageGenerationEvents.FAILED, {
        message_id: message.id,
        chat_id: threadId,
        file_id: fileId,
        error: error.toString(),
        time_to_failure_ms: timeToFailureMs,
      });
    },
    [message.id, threadId]
  );

  const handleImageViewed = useCallback(
    (fileId: string) => {
      trackEvent(ImageGenerationEvents.VIEWED, {
        file_id: fileId,
        message_id: message.id,
        chat_id: threadId,
      });
    },
    [message.id, threadId]
  );

  const handleImageDownloaded = useCallback(
    (fileId: string) => {
      trackEvent(ImageGenerationEvents.DOWNLOADED, {
        file_id: fileId,
        chat_id: threadId,
        message_id: message.id,
        time_since_generation_ms: Date.now() - message.createdAt.getTime(),
      });
    },
    [message.id, threadId, message.createdAt]
  );

  return {
    handleCopyableEdit,
    handleAskSelection,
    handleImageLoaded,
    handleImageError,
    handleImageViewed,
    handleImageDownloaded,
  };
};
